/**
 * gf-stock-hide.js — Versteckt jede Collection-Referenz auf der Seite,
 * sobald die Collection keine verfügbaren Produkte mehr hat.
 *
 * Benutzung:
 *   <element data-collection-handle="my-collection">…</element>
 *
 * Opt-out (für ein Element oder ein Vorfahr-Element):
 *   <element data-no-hide-empty>…</element>
 *
 * Der Cache (sessionStorage, 90 s TTL) wird zwischen allen Aufrufen geteilt,
 * sodass Top-Nav, Sidebar und Kategorie-Grid die gleichen Stock-Daten nutzen.
 */
(function () {
  'use strict';

  var CACHE_PREFIX = 'gf_nav_stock_';
  var CACHE_TTL_MS = 30 * 60 * 1000;  /* 30 Min.: sonst ~1 MB Abfragen bei jedem Seitenaufruf */
  var MAX_CONCURRENT = 5;            // Drosselung: max. gleichzeitige fetch-Requests
  var inflight = {};
  var queue = [];                    // wartende Tasks (function -> Promise)
  var active = 0;                    // aktuell laufende fetch-Requests

  /* Reiht eine fetch-Aufgabe ein und startet sie erst, wenn ein Slot frei ist.
     Verhindert, dass N Collection-Requests gleichzeitig feuern. */
  function enqueue(task) {
    return new Promise(function (resolve) {
      queue.push(function () {
        active++;
        resolve(task().finally(function () {
          active--;
          drainQueue();
        }));
      });
      drainQueue();
    });
  }

  function drainQueue() {
    while (active < MAX_CONCURRENT && queue.length) {
      queue.shift()();
    }
  }

  function getCache(handle) {
    try {
      var raw = localStorage.getItem(CACHE_PREFIX + handle);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (Date.now() - obj.ts > CACHE_TTL_MS) return null;
      return obj.available;
    } catch (e) { return null; }
  }

  function setCache(handle, available) {
    try {
      localStorage.setItem(CACHE_PREFIX + handle, JSON.stringify({
        ts: Date.now(),
        available: available
      }));
    } catch (e) {}
  }

  function isProductAvailable(p) {
    if (!p || !p.variants || !p.variants.length) return false;
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i] && p.variants[i].available) return true;
    }
    return false;
  }

  function checkCollection(handle) {
    /* Vorab-Auskunft vom Server (layout/theme.liquid): sicher ja/nein, ohne Abfrage */
    var known = window.gfAvail && Object.prototype.hasOwnProperty.call(window.gfAvail, handle) ? window.gfAvail[handle] : null;
    if (known === 1) return Promise.resolve(true);
    if (known === 0) return Promise.resolve(false);
    var cached = getCache(handle);
    if (cached !== null) return Promise.resolve(cached);
    if (inflight[handle]) return inflight[handle];
    /* Der eigentliche Netzwerk-Request läuft über die Concurrency-Drossel (enqueue),
       sodass nie mehr als MAX_CONCURRENT Requests gleichzeitig offen sind. */
    inflight[handle] = enqueue(function () {
      return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250')
        .then(function (r) {
          /* Nur eine echte Antwort darf ausblenden. Bei 429 (zu viele Anfragen) oder
             Serverfehlern bleibt alles sichtbar und nichts wird zwischengespeichert. */
          if (!r.ok) { var e = new Error('HTTP ' + r.status); e.status = r.status; throw e; }
          return r.json();
        })
        .then(function (data) {
          var products = data.products || [];
          var available = products.length > 0 && products.some(isProductAvailable);
          setCache(handle, available);
          delete inflight[handle];
          return available;
        })
        .catch(function (err) {
          delete inflight[handle];
          if (window.console && console.warn) console.warn('[gf-stock] failed for', handle, err);
          return true;
        });
    });
    return inflight[handle];
  }

  /* Global API damit andere Scripts den Cache mitnutzen können */
  window.gfStockCheck = checkCollection;

  function isOptedOut(el) {
    return !!(el.closest && el.closest('[data-no-hide-empty]'));
  }

  function hideElement(el) {
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
    el.dataset.gfHidden = 'true';
  }

  function cleanupOrphans() {
    var containers = document.querySelectorAll(
      '.gf-nav, .gfsidebar, .gfsidebar-section, .gfsidebar-nav, ' +
      '.gf-dropdown, .gf-subdropdown, .category-grid, .gfsubmenu'
    );
    Array.prototype.forEach.call(containers, function (cont) {
      var listChildren = cont.querySelectorAll(':scope > li, :scope > a');
      if (!listChildren.length) {
        listChildren = cont.querySelectorAll(':scope > * > li');
      }
      var visible = 0;
      Array.prototype.forEach.call(listChildren, function (c) {
        if (c.style.display !== 'none') visible++;
      });
      if (visible === 0 && listChildren.length > 0 && !cont.dataset.collectionHandle) {
        var parentLi = cont.closest('li');
        if (parentLi && !parentLi.dataset.collectionHandle) {
          hideElement(parentLi);
        } else {
          hideElement(cont);
        }
      }
    });
  }

  /* cacheOnly: nur bereits bekannte Ergebnisse anwenden (sofort, ohne Netzwerk) */
  function run(cacheOnly) {
    cacheOnly = cacheOnly === true;
    var nodes = document.querySelectorAll('[data-collection-handle]:not([data-gf-hidden])');
    if (!nodes.length) return;

    var handles = Object.create(null);
    var elements = [];
    Array.prototype.forEach.call(nodes, function (el) {
      if (isOptedOut(el)) return;
      var h = el.dataset.collectionHandle;
      if (!h) return;
      handles[h] = true;
      elements.push(el);
    });
    if (!elements.length) return;

    var keys = Object.keys(handles);
    if (cacheOnly) {
      keys = keys.filter(function (h) {
        var k = window.gfAvail && Object.prototype.hasOwnProperty.call(window.gfAvail, h);
        return k || getCache(h) !== null;
      });
      if (!keys.length) return;
    }
    var promises = keys.map(function (h) {
      return checkCollection(h).then(function (a) { return { h: h, a: a }; });
    });

    Promise.allSettled(promises).then(function (results) {
      var unavailable = Object.create(null);
      results.forEach(function (r) {
        if (r.status === 'fulfilled' && !r.value.a) unavailable[r.value.h] = true;
      });
      elements.forEach(function (el) {
        if (unavailable[el.dataset.collectionHandle]) hideElement(el);
      });
      cleanupOrphans();
    });
  }

  /* Erst nach dem Laden der Seite und im Leerlauf pruefen: die Abfragen sollen den
     sichtbaren Seitenaufbau (Bilder, Schriften) nicht ausbremsen. */
  function later() {
    if ('requestIdleCallback' in window) { requestIdleCallback(function () { run(false); }, { timeout: 2500 }); }
    else { setTimeout(function () { run(false); }, 300); }
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', function () { run(true); }); }
  else { run(true); }
  if (document.readyState === 'complete') { later(); }
  else { window.addEventListener('load', later); }
})();
